/**
 * Relay-aligned scoring function.
 *
 * Replicates the relay's computeRawScore (scoring.go:80-93) and
 * ComputeDecay (decay.go:10-12) so the client produces identical
 * scores for the same inputs.
 */

/** Weight parameters that mirror the relay's scoring config. */
export interface RelayScoreWeights {
  replyWeight: number;
  repostWeight: number;
  replyCountWeight: number;
  subtractNegative: boolean;
  subtractReports: boolean;
  reportWeight: number;
  decayLambda: number;
}

/** Component breakdown of a score calculation. */
export interface RelayScoreComponents {
  base: number;
  pos: number;
  neg: number;
  report: number;
  replyPow: number;
  repostPow: number;
  replyCount: number;
  reactionCount: number;
  rawTotal: number;
  decayedTotal: number;
}

/** Default weights matching relay/internal/config/defaults.go exactly. */
export const RELAY_FORMULA_DEFAULTS: RelayScoreWeights = {
  replyWeight: 0.5,
  repostWeight: 1.0,
  replyCountWeight: 0.5,
  subtractNegative: false,
  subtractReports: true,
  reportWeight: 1.0,
  decayLambda: 0.1,
};

/**
 * Exponential time decay.
 * Exact match to relay/internal/scoring/decay.go ComputeDecay.
 */
export function computeDecay(rawScore: number, decayLambda: number, ageDays: number): number {
  return rawScore * Math.exp(-decayLambda * ageDays);
}

/**
 * Compute a relay-aligned score from component values and weights.
 * Exact match to relay/internal/scoring/scoring.go computeRawScore
 * plus decay.
 */
export function computeRelayScore(
  components: Omit<RelayScoreComponents, 'rawTotal' | 'decayedTotal'>,
  weights: RelayScoreWeights,
  ageDays: number,
): RelayScoreComponents {
  let raw =
    components.base +
    components.pos +
    weights.replyWeight * components.replyPow +
    weights.repostWeight * components.repostPow +
    weights.replyCountWeight * components.replyCount;

  if (weights.subtractNegative) {
    raw -= components.neg;
  }
  if (weights.subtractReports) {
    raw -= weights.reportWeight * components.report;
  }

  const decayed = computeDecay(raw, weights.decayLambda, ageDays);

  return {
    ...components,
    rawTotal: raw,
    decayedTotal: decayed,
  };
}
